import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { CLUBS_APP_BASE_URL, MAIN_APP_BASE_URL, WALLET_APP_BASE_URL } from '../config/env';
import { useAdminPortal } from '../context/AdminPortalContext';

type AdminIconName =
  | 'overview'
  | 'clubs'
  | 'owners'
  | 'players'
  | 'security'
  | 'realtime'
  | 'billing'
  | 'system'
  | 'incidents'
  | 'config'
  | 'game-modes'
  | 'audit'
  | 'announcements'
  | 'menu'
  | 'logout'
  | 'collapse';

interface NavItem {
  to: string;
  label: string;
  description: string;
  icon: AdminIconName;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/overview', label: 'Overview', description: 'Command center da plataforma', icon: 'overview' },
  { to: '/clubs', label: 'Clubs', description: 'Operadores e saude dos clubs', icon: 'clubs' },
  { to: '/owners', label: 'Owners', description: 'Donos de club e assinatura', icon: 'owners' },
  { to: '/players', label: 'Players', description: 'Jogadores e operacao global', icon: 'players' },
  { to: '/security', label: 'Seguranca', description: 'Fraude, risco e revisao', icon: 'security' },
  { to: '/realtime', label: 'Realtime', description: 'Uso, trafego e websocket', icon: 'realtime' },
  { to: '/billing', label: 'Billing', description: 'Financeiro SaaS e planos', icon: 'billing' },
  { to: '/system', label: 'Sistema', description: 'Saude tecnica e falhas', icon: 'system' },
  { to: '/incidents', label: 'Incidents', description: 'Fila operacional consolidada', icon: 'incidents' },
  { to: '/config', label: 'Config', description: 'Parametros globais', icon: 'config' },
  { to: '/game-modes', label: 'Config modos de jogo', description: 'Tempos por variante', icon: 'config' },
  { to: '/announcements', label: 'Avisos', description: 'Mensagens globais do app', icon: 'announcements' },
  { to: '/audit', label: 'Audit', description: 'Trilha administrativa', icon: 'audit' },
];

const findActiveItem = (pathname: string): NavItem =>
  NAV_ITEMS.find((item) => pathname === item.to || pathname.startsWith(`${item.to}/`)) || NAV_ITEMS[0];

const getInitials = (value: string): string =>
  value
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'A';

const AdminIcon: React.FC<{ name: AdminIconName; className?: string }> = ({ name, className }) => {
  const commonProps = {
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    className,
    'aria-hidden': true,
  };

  switch (name) {
    case 'overview':
      return (
        <svg {...commonProps}>
          <path d="M4 13.5h6.5V20H4zM13.5 4H20v8h-6.5zM13.5 14.5H20V20h-6.5zM4 4h6.5v6.5H4z" />
        </svg>
      );
    case 'clubs':
      return (
        <svg {...commonProps}>
          <path d="M4 10V6.8A1.8 1.8 0 0 1 5.8 5h12.4A1.8 1.8 0 0 1 20 6.8V10" />
          <path d="M4 10h16v8.2A1.8 1.8 0 0 1 18.2 20H5.8A1.8 1.8 0 0 1 4 18.2z" />
          <path d="M9 10V8h6v2" />
        </svg>
      );
    case 'owners':
      return (
        <svg {...commonProps}>
          <path d="M12 11.2a3.7 3.7 0 1 0 0-7.4 3.7 3.7 0 0 0 0 7.4Z" />
          <path d="M5 20a7 7 0 0 1 14 0" />
        </svg>
      );
    case 'players':
      return (
        <svg {...commonProps}>
          <path d="M9 12.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4ZM17 11a2.4 2.4 0 1 0 0-4.8" />
          <path d="M3.8 19.2a5.6 5.6 0 0 1 10.4 0M15.5 18.4a4.4 4.4 0 0 1 4.7-2.7" />
        </svg>
      );
    case 'security':
      return (
        <svg {...commonProps}>
          <path d="M12 3.5 5.5 6v5.3c0 4.3 2.8 7.8 6.5 9.2 3.7-1.4 6.5-4.9 6.5-9.2V6z" />
          <path d="M9.5 12 11 13.5l3.5-3.5" />
        </svg>
      );
    case 'realtime':
      return (
        <svg {...commonProps}>
          <path d="M4 18a8 8 0 1 1 16 0" />
          <path d="M12 12v4M8.5 8.5l1.5 1.5M15.5 8.5 14 10" />
        </svg>
      );
    case 'billing':
      return (
        <svg {...commonProps}>
          <rect x="3.5" y="5" width="17" height="14" rx="3" />
          <path d="M3.5 9h17M8 14.5h3M14 14.5h2.5" />
        </svg>
      );
    case 'system':
      return (
        <svg {...commonProps}>
          <path d="m12 4 1.4 1.8 2.3-.2.8 2.1 2.1.8-.2 2.3L20 12l-1.8 1.4.2 2.3-2.1.8-.8 2.1-2.3-.2L12 20l-1.4-1.8-2.3.2-.8-2.1-2.1-.8.2-2.3L4 12l1.8-1.4-.2-2.3 2.1-.8.8-2.1 2.3.2z" />
          <circle cx="12" cy="12" r="3.2" />
        </svg>
      );
    case 'incidents':
      return (
        <svg {...commonProps}>
          <path d="M12 4.5 19 18H5z" />
          <path d="M12 9v4.5M12 17h.01" />
        </svg>
      );
    case 'config':
      return (
        <svg {...commonProps}>
          <path d="M4 7h10M4 17h16M14 7h6M4 12h16M8 5v4M16 10v4M11 15v4" />
        </svg>
      );
    case 'audit':
      return (
        <svg {...commonProps}>
          <path d="M7 4.5h10l2 2V19a1 1 0 0 1-1 1H7a2 2 0 0 1-2-2V6.5a2 2 0 0 1 2-2Z" />
          <path d="M9 10h6M9 14h6" />
        </svg>
      );
    case 'announcements':
      return (
        <svg {...commonProps}>
          <path d="M18 10.2a6 6 0 0 0-12 0c0 6-2.3 6.8-2.3 6.8h16.6S18 16.2 18 10.2Z" />
          <path d="M10 20h4" />
        </svg>
      );
    case 'menu':
      return (
        <svg {...commonProps}>
          <path d="M4 7h16M4 12h16M4 17h16" />
        </svg>
      );
    case 'logout':
      return (
        <svg {...commonProps}>
          <path d="M9.5 20H6.8A1.8 1.8 0 0 1 5 18.2V5.8A1.8 1.8 0 0 1 6.8 4h2.7" />
          <path d="M14.5 16.5 19 12l-4.5-4.5M10 12h9" />
        </svg>
      );
    case 'collapse':
      return (
        <svg {...commonProps}>
          <path d="m15 18-6-6 6-6" />
          <path d="M19.5 5v14" />
        </svg>
      );
    default:
      return null;
  }
};

const AdminShell: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile, signOut } = useAdminPortal();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const activeItem = useMemo(() => findActiveItem(location.pathname), [location.pathname]);
  const adminName = profile?.nickname || profile?.nome || 'Admin';

  useEffect(() => {
    document.title = `${activeItem.label} | 3BET Admin`;
  }, [activeItem.label]);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    if (mobileNavOpen) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileNavOpen]);

  const handleLogout = () => {
    signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div
      className={[
        'admin-shell',
        sidebarCollapsed ? 'admin-shell--collapsed' : '',
        mobileNavOpen ? 'admin-shell--nav-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="admin-shell__scrim"
        aria-label="Fechar navegacao"
        onClick={() => setMobileNavOpen(false)}
      />

      <aside className="admin-sidebar">
        <div className="admin-sidebar__header">
          <div className="admin-brand">
            <img
              src="/icones/icone-sem-fundo-com-trangulo.png"
              alt="3BET"
              className="admin-brand__logo"
            />
            <div className="admin-brand__copy">
              <p className="admin-brand__eyebrow">Platform control</p>
              <h1 className="admin-brand__title">3BET Admin</h1>
              <p className="admin-brand__meta">Command center do SaaS</p>
            </div>
          </div>

          <button
            type="button"
            className="admin-icon-button admin-icon-button--desktop"
            aria-label={sidebarCollapsed ? 'Expandir menu' : 'Recolher menu'}
            onClick={() => setSidebarCollapsed((current) => !current)}
          >
            <AdminIcon name="collapse" />
          </button>
        </div>

        <nav className="admin-nav" aria-label="Navegacao principal do portal admin">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                `admin-nav__link${isActive ? ' admin-nav__link--active' : ''}`
              }
            >
              <span className="admin-nav__icon">
                <AdminIcon name={item.icon} />
              </span>
              <span className="admin-nav__copy">
                <strong>{item.label}</strong>
                <small>{item.description}</small>
              </span>
            </NavLink>
          ))}
        </nav>

        <div className="admin-sidebar__footer">
          <div className="admin-sidebar__status">
            <span className="admin-status-dot" />
            <div>
              <strong>Camada global ativa</strong>
              <span>Separada do portal do club</span>
            </div>
          </div>
          <div className="admin-sidebar__profile">
            <span className="admin-profile-avatar">{getInitials(adminName)}</span>
            <div>
              <strong>{adminName}</strong>
              <span>ADMIN</span>
            </div>
          </div>
        </div>
      </aside>

      <div className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar__lead">
            <button
              type="button"
              className="admin-icon-button admin-icon-button--mobile"
              aria-label="Abrir navegacao"
              onClick={() => setMobileNavOpen(true)}
            >
              <AdminIcon name="menu" />
            </button>

            <div className="admin-topbar__title">
              <p className="admin-section-eyebrow">3BET Platform</p>
              <h2>{activeItem.label}</h2>
              <span>{activeItem.description}</span>
            </div>
          </div>

          <div className="admin-topbar__actions">
            <a href={MAIN_APP_BASE_URL} className="admin-link-chip" target="_blank" rel="noreferrer">
              App
            </a>
            <a href={CLUBS_APP_BASE_URL} className="admin-link-chip" target="_blank" rel="noreferrer">
              Clubs
            </a>
            <a href={WALLET_APP_BASE_URL} className="admin-link-chip" target="_blank" rel="noreferrer">
              Wallet
            </a>
            <button type="button" className="admin-secondary-button" onClick={handleLogout}>
              <AdminIcon name="logout" className="admin-button__icon" />
              Sair
            </button>
          </div>
        </header>

        <main className="admin-content">
          <Outlet />
        </main>

        <footer className="admin-footer">
          <span>3BET Admin</span>
          <span>Operacao global, billing, risco e observabilidade</span>
        </footer>
      </div>
    </div>
  );
};

export default AdminShell;
